import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useUser } from '../../contexts/UserContext';
import { isLscBillingUiEnabled } from '../../config/featureFlags';
import { getLscBillingStatus, LscApiError } from '../api';
import type { LscBillingStatusResponse } from '../types';
import FounderBetaPricingCards from '../components/FounderBetaPricingCards';
import BillingDisabledBanner from '../components/BillingDisabledBanner';
import BillingSupportContact from '../billingSupportContact';
import LoadingState from '../components/LoadingState';
import { LSC_PRICING_SUMMARY_LINE } from '../billingCopy';
import {
  LANDING_COMMISSIONER_LANE_CTA,
  LANDING_MEMBER_LANE_CTA,
  LANDING_MEMBER_LANE_DETAIL,
  LANDING_MEMBER_LANE_TITLE,
} from '../landing/landingCopy';
import { resolveLscBillingPresentation } from '../billingPresentation';
import { lscPath, lscReturnTo } from '../routing';
import { useLscContentColumn } from '../lscContentColumn';

const LscPricingPage: React.FC = () => {
  useLscContentColumn('narrow');
  const { user, loading: authLoading, signInWithTwitch } = useUser();
  const billingUiEnabled = isLscBillingUiEnabled();
  const [billingStatus, setBillingStatus] = useState<LscBillingStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    if (!user) {
      setBillingStatus(null);
      setLoadError(null);
      return;
    }
    setStatusLoading(true);
    setLoadError(null);
    try {
      const status = await getLscBillingStatus();
      setBillingStatus(status);
    } catch (err) {
      if (err instanceof LscApiError && (err.status === 401 || err.status === 403)) {
        setBillingStatus(null);
        setLoadError(null);
      } else {
        setLoadError('Billing information is unavailable right now.');
      }
    } finally {
      setStatusLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const presentation = useMemo(
    () =>
      resolveLscBillingPresentation({
        status: billingStatus,
        surface: user ? 'account_pricing' : 'public_pricing',
        viewer: { isAuthenticated: Boolean(user), isOwner: true },
        context: { billingUiEnabled },
      }),
    [billingStatus, billingUiEnabled, user]
  );

  if (authLoading) {
    return <LoadingState message="Loading pricing..." />;
  }

  return (
    <div className="space-y-6 max-w-2xl" data-testid="lsc-pricing-page">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">{presentation.headline}</h1>
        <p className="text-sm text-secondary-400">{presentation.valueProposition}</p>
        <p className="text-sm text-primary-200/90 mt-2">{LSC_PRICING_SUMMARY_LINE}</p>
      </div>

      {presentation.mode === 'anonymous' && (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-900/50 px-4 py-3 space-y-3"
          data-testid="lsc-pricing-sign-in-prompt"
        >
          <p className="text-sm text-secondary-300">
            {presentation.detailLines[0] ||
              'Sign in with Twitch to subscribe as a commissioner. Members can join a league for free with an invite code.'}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-primary"
              onClick={() => signInWithTwitch(lscReturnTo('/pricing'))}
              data-testid="lsc-pricing-sign-in-cta"
            >
              {presentation.primaryCta?.label || 'Sign in to subscribe'}
            </button>
            <Link to={lscPath('/join')} className="btn-secondary" data-testid="lsc-pricing-join-cta">
              {LANDING_MEMBER_LANE_CTA}
            </Link>
          </div>
        </div>
      )}

      <div
        className="rounded-lg border border-secondary-700 bg-secondary-900/30 px-4 py-3 space-y-2"
        data-testid="lsc-pricing-acquisition-lanes"
      >
        <h2 className="text-sm font-medium text-white">{LANDING_MEMBER_LANE_TITLE}</h2>
        <p className="text-xs text-secondary-400">{LANDING_MEMBER_LANE_DETAIL}</p>
        <Link to={lscPath('/join')} className="btn-secondary text-sm inline-flex">
          {LANDING_MEMBER_LANE_CTA}
        </Link>
        <h2 className="text-sm font-medium text-white pt-2">{LANDING_COMMISSIONER_LANE_CTA}</h2>
        <p className="text-xs text-secondary-400">
          Commissioner subscription required to create and manage a league workspace.
        </p>
      </div>

      {presentation.mode === 'ui_disabled' && user && (
        <BillingDisabledBanner variant="ui" message={presentation.disabledReason} />
      )}

      {presentation.mode === 'subscribed_bound' && (
        <div
          className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 px-4 py-3 space-y-2"
          data-testid="lsc-pricing-subscribed-bound"
          role="status"
        >
          {presentation.detailLines.map((line) => (
            <p key={line} className="text-sm text-emerald-100">
              {line}
            </p>
          ))}
          {presentation.primaryCta?.href && (
            <Link
              to={presentation.primaryCta.href}
              className="text-sm text-primary-300 hover:text-primary-200 inline-flex min-h-11 items-center"
            >
              {presentation.primaryCta.label}
            </Link>
          )}
        </div>
      )}

      {presentation.mode === 'paid_unbound' && (
        <div
          className="rounded-lg border border-amber-700/60 bg-amber-950/40 px-4 py-3 space-y-2"
          data-testid="lsc-pricing-paid-unbound"
          role="status"
        >
          {presentation.detailLines.map((line) => (
            <p key={line} className="text-sm text-amber-100">
              {line}
            </p>
          ))}
          {presentation.primaryCta?.href && (
            <Link
              to={presentation.primaryCta.href}
              className="btn-primary inline-flex min-h-11 items-center"
            >
              {presentation.primaryCta.label}
            </Link>
          )}
        </div>
      )}

      {presentation.disabledBanner === 'lifecycle' &&
        presentation.mode !== 'subscribed_bound' &&
        presentation.mode !== 'paid_unbound' &&
        user && (
          <BillingDisabledBanner variant="checkout" message={presentation.disabledReason} />
        )}

      {user && statusLoading ? (
        <LoadingState message="Loading billing status…" />
      ) : (
        presentation.showPricingCards && (
          <FounderBetaPricingCards
            billingStatus={billingStatus}
            showCheckout={billingUiEnabled}
            presentation={presentation}
          />
        )
      )}

      {loadError && (
        <p className="text-sm text-secondary-400" role="status">
          {loadError}
        </p>
      )}

      {user && presentation.secondaryCtas.length > 0 && (
        <p className="text-sm text-secondary-500">
          {presentation.mode === 'subscribed_bound' ? 'Need billing details? ' : 'Already subscribed? '}
          {presentation.secondaryCtas.map((cta, index) => (
            <React.Fragment key={cta.code}>
              {index > 0 && ' · '}
              {cta.href ? (
                <Link to={cta.href} className="text-primary-300 hover:text-primary-200">
                  {cta.label}
                </Link>
              ) : (
                cta.label
              )}
            </React.Fragment>
          ))}
        </p>
      )}

      <BillingSupportContact />
    </div>
  );
};

export default LscPricingPage;
